'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { gatewayClient } from '../api';
import { useAuth } from './AuthContext';
import type { WishlistSummaryItem } from '../types/wishlist';

interface WishlistContextType {
  productIds: Set<string>;
  isHydrated: boolean;
  isInWishlist: (productId: string) => boolean;
  add: (productId: string) => Promise<void>;
  remove: (productId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

const STORAGE_KEY = 'aceb_wishlist_v1';

type CacheShape = { userId: string; items: WishlistSummaryItem[] };

function readCache(userId: string): WishlistSummaryItem[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheShape;
    if (!parsed || parsed.userId !== userId || !Array.isArray(parsed.items)) return null;
    return parsed.items;
  } catch {
    return null;
  }
}

function writeCache(userId: string, items: WishlistSummaryItem[]) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ userId, items }));
  } catch {
    // ignore quota errors
  }
}

function clearCache() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function WishlistProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const { user, isLoading: authLoading } = useAuth();
  const [items, setItems] = useState<WishlistSummaryItem[]>([]);
  const [productIds, setProductIds] = useState<Set<string>>(new Set());
  const [isHydrated, setIsHydrated] = useState(false);
  const hydratedForUser = useRef<string | null | undefined>(undefined);

  const applyItems = useCallback((next: WishlistSummaryItem[]) => {
    setItems(next);
    setProductIds(new Set(next.map((i) => i.productId)));
  }, []);

  const refresh = useCallback(async () => {
    if (!user) {
      applyItems([]);
      clearCache();
      setIsHydrated(true);
      return;
    }
    try {
      const fresh = await gatewayClient.wishlist.getSummary();
      applyItems(fresh);
      writeCache(user.id, fresh);
    } catch {
      // keep whatever we had (cache or empty)
    } finally {
      setIsHydrated(true);
    }
  }, [user, applyItems]);

  useEffect(() => {
    if (authLoading) return;
    const userKey = user?.id ?? null;
    if (hydratedForUser.current === userKey) return;
    hydratedForUser.current = userKey;

    if (!user) {
      applyItems([]);
      clearCache();
      setIsHydrated(true);
      return;
    }

    // Trust the cache when present - it's kept in sync by add/remove.
    // Only hit the BE when we have no cache for this user (first login on this device).
    const cached = readCache(user.id);
    if (cached) {
      applyItems(cached);
      setIsHydrated(true);
      return;
    }
    refresh();
  }, [authLoading, user, applyItems, refresh]);

  const add = useCallback(async (productId: string) => {
    if (!productId || !user) return;
    // optimistic
    setItems((prev) => {
      if (prev.some((p) => p.productId === productId)) return prev;
      const next = [...prev, { productId, slug: '' }];
      writeCache(user.id, next);
      return next;
    });
    setProductIds((prev) => {
      const next = new Set(prev);
      next.add(productId);
      return next;
    });
    try {
      await gatewayClient.wishlist.addToWishlist(productId);
    } catch (err: unknown) {
      const status = (err as { statusCode?: number } | null)?.statusCode;
      if (status !== 409) {
        setItems((prev) => {
          const next = prev.filter((p) => p.productId !== productId);
          writeCache(user.id, next);
          return next;
        });
        setProductIds((prev) => {
          const next = new Set(prev);
          next.delete(productId);
          return next;
        });
        throw err;
      }
    }
  }, [user]);

  const remove = useCallback(async (productId: string) => {
    if (!productId || !user) return;
    const prevItems = items;
    // optimistic
    setItems((prev) => {
      const next = prev.filter((p) => p.productId !== productId);
      writeCache(user.id, next);
      return next;
    });
    setProductIds((prev) => {
      const next = new Set(prev);
      next.delete(productId);
      return next;
    });
    try {
      await gatewayClient.wishlist.removeByProductId(productId);
    } catch (err: unknown) {
      const status = (err as { statusCode?: number } | null)?.statusCode;
      if (status !== 404) {
        applyItems(prevItems);
        writeCache(user.id, prevItems);
        throw err;
      }
    }
  }, [items, applyItems, user]);

  const isInWishlist = useCallback((productId: string) => productIds.has(productId), [productIds]);

  return (
    <WishlistContext.Provider value={{ productIds, isHydrated, isInWishlist, add, remove, refresh }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be used within WishlistProvider');
  return ctx;
}
